import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowCounterClockwise, Buildings, CaretRight, Plus, Warning } from '@phosphor-icons/react';
import {
  listMyOrganizations,
  listMyPendingDeletionOrganizations,
  restoreOrganization,
} from '../api/orgs';
import type { Organization, PendingDeletionOrganization } from '../api/orgs';
import { ApiError } from '../api/client';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { OrgInitial } from '@/components/shell/OrgInitial';
import logoUrl from '../assets/logo-sportadmin.svg';
import logoLimeUrl from '../assets/logo-sportadmin-lime.svg';

export function Landing() {
  const auth = useAuth();
  const { t } = useTranslation('common');
  const token = auth.user?.access_token ?? null;

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });

  const pendingQuery = useQuery({
    queryKey: ['my-orgs-pending-deletion'],
    queryFn: () => listMyPendingDeletionOrganizations(token),
    enabled: !!token,
  });

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <picture className="mx-auto block">
        <source srcSet={logoLimeUrl} media="(prefers-color-scheme: dark)" />
        <img
          src={logoUrl}
          alt={t('app.title')}
          className="block h-12 w-auto md:h-14"
        />
      </picture>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('landing.title')}
        </h1>
        <Button
          type="button"
          variant="outline"
          onClick={() => void auth.signoutRedirect()}
        >
          {t('auth.signOut')}
        </Button>
      </header>

      {orgsQuery.isLoading && (
        <p className="text-sm text-muted-foreground">{t('landing.loading')}</p>
      )}

      {orgsQuery.isError && (
        <>
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {t('landing.loadError')}
          </p>
          <CreateOrgButton />
        </>
      )}

      {orgsQuery.data && orgsQuery.data.length === 0 && <LandingEmpty />}

      {orgsQuery.data && orgsQuery.data.length > 0 && (
        <OrgList organizations={orgsQuery.data} />
      )}

      {pendingQuery.data && pendingQuery.data.length > 0 && (
        <PendingDeletionList organizations={pendingQuery.data} />
      )}
    </main>
  );
}

function LandingEmpty() {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      icon={<Buildings weight="duotone" />}
      title={t('landing.empty.title')}
      body={t('landing.empty.body')}
      actions={[
        {
          label: t('landing.empty.cta'),
          to: '/onboarding',
          icon: <Plus weight="bold" />,
        },
      ]}
    />
  );
}

function OrgList({ organizations }: { organizations: Organization[] }) {
  const { t } = useTranslation('common');
  return (
    <section className="flex flex-col gap-4">
      <h2 className="font-heading text-lg font-bold">{t('landing.list.title')}</h2>
      <ul className="flex flex-col gap-3">
        {organizations.map((org) => (
          <li key={org.id}>
            <Link
              to={`/orgs/${org.slug}`}
              className="group flex items-center gap-4 rounded-2xl border border-border bg-background p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:p-5"
            >
              <OrgInitial
                name={org.name}
                logoKey={org.logo_object_key}
                className="h-12 w-12 text-lg md:h-14 md:w-14 md:text-xl"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading text-base font-bold text-foreground md:text-lg">
                  {org.city ? `${org.name} — ${org.city}` : org.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t(`role.${org.role}`)}
                </div>
              </div>
              <CaretRight
                className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
      <CreateOrgButton />
    </section>
  );
}

function CreateOrgButton() {
  const { t } = useTranslation('common');
  return (
    <Button asChild size="lg" className="mt-2 self-center">
      <Link to="/onboarding">
        <Plus className="h-5 w-5" />
        {t('landing.list.create')}
      </Link>
    </Button>
  );
}

function PendingDeletionList({ organizations }: { organizations: PendingDeletionOrganization[] }) {
  const { t } = useTranslation('common');
  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center gap-2">
        <Warning className="h-5 w-5 text-destructive" weight="fill" aria-hidden />
        <h2 className="font-heading text-lg font-bold">{t('landing.pendingDeletion.title')}</h2>
      </header>
      <p className="text-sm text-muted-foreground">{t('landing.pendingDeletion.subtitle')}</p>
      <ul className="flex flex-col gap-3">
        {organizations.map((org) => (
          <PendingDeletionRow key={org.id} org={org} />
        ))}
      </ul>
    </section>
  );
}

function PendingDeletionRow({ org }: { org: PendingDeletionOrganization }) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const daysRemaining = Math.max(
    0,
    Math.ceil((new Date(org.restore_deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );

  const restoreMutation = useMutation({
    mutationFn: () => restoreOrganization(token, org.slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['my-orgs-pending-deletion'] });
    },
  });

  const errorCode =
    restoreMutation.error instanceof ApiError ? restoreMutation.error.code : 'unknown';

  return (
    <li className="flex flex-col gap-3 rounded-2xl border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:gap-4 md:p-5">
      <OrgInitial
        name={org.name}
        logoKey={org.logo_object_key}
        className="h-12 w-12 text-lg opacity-60 md:h-14 md:w-14 md:text-xl"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-heading text-base font-bold text-foreground/70 line-through md:text-lg">
          {org.city ? `${org.name} — ${org.city}` : org.name}
        </div>
        <div className="text-sm text-muted-foreground">
          {t('landing.pendingDeletion.daysRemaining', { count: daysRemaining })}
        </div>
        {restoreMutation.isError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {t(`landing.pendingDeletion.error.${errorCode}`, {
              defaultValue: t('landing.pendingDeletion.error.unknown'),
            })}
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => restoreMutation.mutate()}
        disabled={restoreMutation.isPending}
        className="shrink-0"
      >
        <ArrowCounterClockwise className="h-4 w-4" />
        {restoreMutation.isPending
          ? t('landing.pendingDeletion.restoring')
          : t('landing.pendingDeletion.restore')}
      </Button>
    </li>
  );
}
